// frontend/src/components/purchase-orders/RecommendationRowActions.jsx
//
// One row of the Purchase recommendations grid, drawn by the shared
// <GridRowActions>: the row's next step (pages/recommendationRowActions.js
// decides which), then a ⋯ menu with the recommendation's lines as CSV or
// Excel — the sheet a merchant works in Excel or sends a supplier before the
// PO exists. The files are in the menu on every row, a blocked one included.
//
// No PDF entry, unlike a PO's menu. A recommendation is a proposal the engine
// made, not a document anyone has committed to; printing one for a supplier
// would send them an order that does not exist yet. The backend has no PDF
// route here either.
//
// Fetch, busy state and toasts come from useBlobDownload — the same mechanics
// the PO menu uses, so a fix to either reaches both.

import { useCallback } from 'react';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useBlobDownload } from '../../hooks/useBlobDownload';
import { recommendationDownloadFilename } from '../../utils/download';
import StatusChip from '../common/StatusChip';
import GridRowActions, { GridPrimaryButton } from '../grid/GridRowActions';
import { recommendationRowActions, ROW_PRIMARY } from '../../pages/recommendationRowActions';

const ENDPOINT = '/api/po-calculations/recommendations/';

/**
 * @param {object}   recommendation  The grid row: PORecommendationID,
 *                                   supplier_name (may be empty — supplier is
 *                                   nullable), RecommendedOrderDate,
 *                                   erp_order_block.
 * @param {Function} onCreatePO      (recommendation) => void
 * @param {Function} onLinkSupplier  (recommendation) => void
 */
export default function RecommendationRowActions({ recommendation, onCreatePO, onLinkSupplier }) {
  const { t } = useTranslation();
  const { busy, download } = useBlobDownload();
  const {
    PORecommendationID: recommendationId,
    supplier_name: supplierName,
    RecommendedOrderDate: orderDate,
  } = recommendation;
  const { primary, blockReason } = recommendationRowActions(recommendation.erp_order_block);

  const downloadLines = useCallback((format) => download({
    url: `${ENDPOINT}${recommendationId}/export/?file_format=${format}`,
    filename: recommendationDownloadFilename(supplierName, orderDate, format),
    failureKey: 'purchaseRecommendations.messages.itemsFailed',
    successKey: 'purchaseRecommendations.messages.itemsSuccess',
    values: { supplier: supplierName },
  }), [download, recommendationId, supplierName, orderDate]);

  const primaryLabels = [
    t('purchaseRecommendations.actions.reviewAndCreate'),
    t('purchaseRecommendations.actions.linkSupplier'),
  ];

  return (
    <GridRowActions
      primary={
        primary === ROW_PRIMARY.CREATE_PO ? (
          <GridPrimaryButton
            label={t('purchaseRecommendations.actions.reviewAndCreate')}
            alternatives={primaryLabels}
            onClick={() => onCreatePO(recommendation)}
          />
        ) : primary === ROW_PRIMARY.LINK_SUPPLIER ? (
          <Tooltip describeChild title={t('purchaseRecommendations.orderBlock.supplier_not_linked')}>
            <GridPrimaryButton
              label={t('purchaseRecommendations.actions.linkSupplier')}
              alternatives={primaryLabels}
              onClick={() => onLinkSupplier(recommendation)}
            />
          </Tooltip>
        ) : (
          <Tooltip describeChild title={t(`purchaseRecommendations.orderBlock.${blockReason}`)}>
            <StatusChip color="warning" label={t('purchaseRecommendations.orderBlock.blockedChip')} />
          </Tooltip>
        )
      }
      items={[
        {
          key: 'csv',
          label: t('purchaseRecommendations.actions.downloadCsv'),
          disabled: busy,
          onClick: () => downloadLines('csv'),
        },
        {
          key: 'xlsx',
          label: t('purchaseRecommendations.actions.downloadExcel'),
          disabled: busy,
          onClick: () => downloadLines('xlsx'),
        },
      ]}
      menuLoading={busy}
    />
  );
}
